import { isFunction } from 'lodash-es';
import semverGreaterThanOrEqual from 'semver/functions/gte';

import { ERR, ApplicationError } from '@shared/application-error';
import { AppConfig } from './app-config';
import type { NativeAppEvent } from './types';

type AndroidWebview = {
  goBackToMenu?: () => void;
  sendAnalyticsEvent?: (e: string) => void;
  sendAnalyticEventToAmplitude?: (e: string) => void;
  requestAppConfig?: () => void;
  requestABTestParameterValue?: (abTestKeys: string) => void;
  openLink?: (link: string) => void;
  openLinkInBrowser?: (link: string) => void;
  onCreated?: () => void;
};
type AndroidWebviewMethod = keyof AndroidWebview;

declare global {
  interface Window {
    AviataAppAndroid?: AndroidWebview;
  }
}

type WithMethod<MethodName extends AndroidWebviewMethod> = Required<
  Pick<AndroidWebview, MethodName>
>;
/**
 * @description Method to check if a given method is available in the Android webview
 *
 * @param androidWebview - Android webview object. It is required (not optional), but can be undefined, so we
 *   explicitly set undefined as a possible value
 * @param methodName - Method name to check
 */
function hasMethod<MethodName extends AndroidWebviewMethod>(
  androidWebview: AndroidWebview | undefined,
  methodName: MethodName,
): androidWebview is WithMethod<MethodName> {
  return (
    window.AviataAppAndroid !== undefined &&
    isFunction(window.AviataAppAndroid[methodName])
  );
}

export function isAndroid() {
  const userAgent = navigator.userAgent.toLowerCase();
  const ios = /iphone|ipod|ipad/.test(userAgent);

  return !ios;
}

export const AndroidWebview = {
  /**
   * @description Method to open link in webview
   *
   * For example, when we need to open some link in webview, without navigating to other apps, e.g browser
   */
  openLink(link: string) {
    if (hasMethod(window.AviataAppAndroid, 'openLink')) {
      window.AviataAppAndroid.openLink(link);
    } else {
      throw new ApplicationError(ERR.NATIVE_API_UNIMPLEMENTED_METHOD, {
        cause: { appVersion: AppConfig.version, methodName: 'openLink' },
      });
    }
  },
  /**
   * @description Sometimes we need to open a link in a browser, not in webview, e.g WhatsApp
   *
   * Method added in 3.6.1 version of the Android app
   */
  openLinkInBrowser(link: string) {
    if (
      AppConfig.version &&
      semverGreaterThanOrEqual(AppConfig.version, '3.6.1') &&
      hasMethod(window.AviataAppAndroid, 'openLinkInBrowser')
    ) {
      window.AviataAppAndroid.openLinkInBrowser(link);
    } else {
      throw new ApplicationError(ERR.NATIVE_API_UNSUPPORTED_VERSION, {
        cause: {
          appVersion: AppConfig.version,
          methodName: 'openLinkInBrowser',
        },
      });
    }
  },
  goBackToMenu() {
    if (hasMethod(window.AviataAppAndroid, 'goBackToMenu')) {
      window.AviataAppAndroid.goBackToMenu();
    }
  },
  sendAnalyticsEvent(event: NativeAppEvent<true>) {
    if (hasMethod(window.AviataAppAndroid, 'sendAnalyticsEvent')) {
      window.AviataAppAndroid.sendAnalyticsEvent(JSON.stringify(event));
    } else {
      throw new ApplicationError(ERR.NATIVE_API_UNIMPLEMENTED_METHOD, {
        cause: {
          appVersion: AppConfig.version,
          methodName: 'sendAnalyticsEvent',
        },
      });
    }
  },
  sendAnalyticEventToAmplitude({
    eventName,
    params,
  }: {
    eventName: string;
    params: Record<string, unknown>;
  }) {
    if (hasMethod(window.AviataAppAndroid, 'sendAnalyticEventToAmplitude')) {
      window.AviataAppAndroid.sendAnalyticEventToAmplitude(
        JSON.stringify({
          eventName,
          params,
        }),
      );
    } else {
      throw new ApplicationError(ERR.NATIVE_API_UNIMPLEMENTED_METHOD, {
        cause: {
          appVersion: AppConfig.version,
          methodName: 'sendAnalyticEventToAmplitude',
        },
      });
    }
  },
  requestAppConfig() {
    if (hasMethod(window.AviataAppAndroid, 'requestAppConfig')) {
      window.AviataAppAndroid.requestAppConfig();
    }
  },
  requestABTestParameterValue(keys: Array<string>) {
    if (hasMethod(window.AviataAppAndroid, 'requestABTestParameterValue')) {
      window.AviataAppAndroid.requestABTestParameterValue(JSON.stringify(keys));
    } else {
      throw new ApplicationError(ERR.NATIVE_API_UNIMPLEMENTED_METHOD, {
        cause: {
          appVersion: AppConfig.version,
          methodName: 'requestABTestParameterValue',
        },
      });
    }
  },
  onCreated() {
    if (hasMethod(window.AviataAppAndroid, 'onCreated')) {
      window.AviataAppAndroid.onCreated();
    } else {
      throw new ApplicationError(ERR.NATIVE_API_UNIMPLEMENTED_METHOD, {
        cause: {
          appVersion: AppConfig.version,
          methodName: 'onCreated',
        },
      });
    }
  },
};
